// src/pages/GfmOrderBuilder.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';
import SupplierSelect from '../components/SupplierSelect';
import Icon from '../components/Icon';
import { GFM_SITES, GFM_SITE_KEYS } from '../constants/gfm';

function capWords(s = '') {
  return s.replace(/(^\w|\s\w)/g, c => c.toUpperCase());
}

export default function GfmOrderBuilder(){
  const cmd = useCommands();
  const nav = useNavigate();
  const loc = useLocation();
  const q = new URLSearchParams(loc.search);

  const preSite = (q.get('site') || 'main').toLowerCase();
  const preItem = q.get('item') || '';
  const [site, setSite] = useState(GFM_SITE_KEYS.includes(preSite) ? preSite : 'main');

  const [orderedDate, setOrderedDate] = useState(new Date().toISOString().slice(0,10));
  const [supplier, setSupplier] = useState('');
  const [orderedBy, setOrderedBy] = useState('');
  const [notes, setNotes] = useState('');

  // mode: single or bulk
  const [mode, setMode] = useState(preItem ? 'single' : 'single');

  // inventory cache
  const [invRows, setInvRows] = useState([]);
  const [loadingInv, setLoadingInv] = useState(true);
  const [err, setErr] = useState('');

  // SINGLE
  const [singleItem, setSingleItem] = useState(preItem);
  const [singleQty, setSingleQty] = useState(1);
  const [singleUnitCost, setSingleUnitCost] = useState(0);
  const singlePriceTouched = useRef(false); // don’t overwrite user-entered price

  // BULK builders
  const [selName, setSelName] = useState('');
  const [qty, setQty] = useState(1);
  const [unitCost, setUnitCost] = useState(0);
  const [lines, setLines] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingInv(true);
      setErr('');
      try{
        const data = await cmd.inventory.get(site);
        if (mounted) setInvRows(Array.isArray(data) ? data : []);
      }catch(e){
        if (mounted) setInvRows([]);
      }finally{
        if (mounted) setLoadingInv(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  useEffect(() => {
    if (preItem) {
      setMode('single');
      setSingleItem(preItem);
    }
  }, [preItem]);

  // quick index for price/pack lookups (case-insensitive)
  const invIndex = useMemo(() => {
    const idx = new Map();
    for (const r of invRows) {
      idx.set(String(r.item || '').toLowerCase(), r);
    }
    return idx;
  }, [invRows]);

  const itemNames = useMemo(
    () => (invRows ?? []).map(r => r.item).sort((a,b) => a.localeCompare(b)),
    [invRows]
  );

  const totalCost = useMemo(
    () => lines.reduce((sum, l) => sum + Number(l.amount||0) * Number(l.unitCost||0), 0),
    [lines]
  );

  // ------------- SINGLE: auto-fill price from inventory -------------
  const singleMeta = useMemo(() => {
    const r = invIndex.get(String(singleItem || '').toLowerCase());
    return r ? {
      unitPrice: Number(r.unitPrice || 0),
      packOf: Math.max(1, Number(r.packOf || 1))
    } : { unitPrice: 0, packOf: 1 };
  }, [invIndex, singleItem]);

  useEffect(() => {
    if (!singlePriceTouched.current) {
      if (singleMeta.unitPrice > 0) setSingleUnitCost(singleMeta.unitPrice);
    }
  }, [singleMeta.unitPrice]);

  function onSingleUnitCostChange(v){
    singlePriceTouched.current = true;
    setSingleUnitCost(Math.max(0, Number(v||0)));
  }

  // ------------- BULK: add line, default price from inventory if left 0 -------------
  function addLine(){
    const name = capWords(String(selName || '').trim());
    const amount = Math.max(1, Number(qty || 1));
    const inv = invIndex.get(name.toLowerCase());
    const price = Number(unitCost || 0) || Number(inv?.unitPrice || 0); // fall back to inv price
    if (!name || !amount) return;

    setLines(prev => {
      const idx = prev.findIndex(x => x.item.toLowerCase() === name.toLowerCase());
      if (idx >= 0){
        const next = [...prev];
        const merged = { ...next[idx] };
        merged.amount = Number(merged.amount||0) + amount;
        // if new price > 0, overwrite; else keep existing
        merged.unitCost = price > 0 ? price : (merged.unitCost || 0);
        next[idx] = merged;
        return next;
      }
      return [...prev, { item: name, amount, unitCost: Math.max(0, price) }];
    });

    setSelName(''); setQty(1); setUnitCost(0);
  }

  function updateQty(item, v){
    const n = Math.max(1, Number(v||1));
    setLines(prev => prev.map(l => l.item===item ? { ...l, amount: n } : l));
  }
  function updatePrice(item, v){
    const n = Math.max(0, Number(v||0));
    setLines(prev => prev.map(l => l.item===item ? { ...l, unitCost: n } : l));
  }
  function removeLine(item){
    setLines(prev => prev.filter(l => l.item !== item));
  }

  async function submitBulk(){
    setErr('');

    if (!supplier.trim()) {
      setErr('Supplier is required.');
      return;
    }

    if (!lines.length) { setErr('Add at least one item.'); return; }
    const payload = {
      site,
      orderedDate: new Date(orderedDate),
      orderedBy: orderedBy?.trim() || '',
      supplier: supplier?.trim() || '',
      notes: notes?.trim() || '',
      lines: lines.map(l => ({
        item: l.item,
        amount: Number(l.amount||0),
        unitCost: Number(l.unitCost||0),
        supplier: supplier?.trim() || ''
      })),
      totalCost: lines.reduce((s,l)=>s + Number(l.amount||0)*Number(l.unitCost||0), 0)
    };

    try{
      if (!cmd?.gfmDeliveries?.create)
        throw new Error('commands.gfmDeliveries.create not implemented');
      await cmd.gfmDeliveries.create(payload);
      nav('/gfm/deliveries');
    }catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to create order');
    }
  }

  async function submitSingle(){
    setErr('');

    if (!supplier.trim()) {
      setErr('Supplier is required.');
      return;
    }
    const name = capWords(String(singleItem || '').trim());
    const qty  = Math.max(1, Number(singleQty || 1));
    const cost = Math.max(0, Number(singleUnitCost || 0));
    if (!name) { setErr('Enter an item name.'); return; }

    const payload = {
      site,
      orderedDate: new Date(orderedDate),
      orderedBy: orderedBy?.trim() || '',
      supplier: supplier?.trim() || '',
      notes: notes?.trim() || '',
      item: name,
      amount: qty,
      unitCost: cost
    };

    try{
      if (!cmd?.gfmDeliveries?.create)
        throw new Error('commands.gfmDeliveries.create not implemented');
      await cmd.gfmDeliveries.create(payload);
      nav('/gfm/deliveries');
    }catch(e){
      setErr(e?.response?.data?.error || e?.message || 'Failed to create order');
    }
  }

  // UI helpers
  function packHint(meta, qty = 1) {
    if (!meta || meta.packOf <= 1) return null;
    const each = meta.unitPrice > 0 ? (meta.unitPrice / meta.packOf) : 0;
    const totalUnits = Number(qty || 1) * meta.packOf;
    return (
      <div className="line-hint">
        Pack of {meta.packOf}. {totalUnits} units will be added to stock.
        {meta.unitPrice > 0 && ` £${meta.unitPrice.toFixed(2)} per pack, about £${each.toFixed(2)} each.`}
      </div>
    );
  }

  const bulkSelMeta = useMemo(() => {
    const r = invIndex.get(String(selName || '').toLowerCase());
    return r ? { unitPrice: Number(r.unitPrice||0), packOf: Math.max(1, Number(r.packOf||1)) } : { unitPrice:0, packOf:1 };
  }, [invIndex, selName]);

  const supplierRequired = !supplier.trim();

  return (
    <div className="container">
      <PageHeader
        eyebrow="GFM"
        title="New order"
        actions={
          <>
            <Link to={`/inventory/${site}`} className="btn secondary">Inventory</Link>
            <Link to="/gfm/deliveries" className="btn secondary">Orders</Link>
          </>
        }
      />

      {err && <div className="page-alerts"><div className="alert error">{err}</div></div>}

      <div className="card mb-16">
        <div className="card-head">
          <h2 className="card-title">Order details</h2>
        </div>
        <div className="form-grid cols-4">
          <label className="input-label">Site
            <select className="input" value={site} onChange={e=>setSite(e.target.value)}>
              {GFM_SITES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>

          <label className="input-label">Ordered date
            <input className="input" type="date" value={orderedDate} onChange={e=>setOrderedDate(e.target.value)} />
          </label>

          <label className="input-label">Supplier <span className="req">*</span>
            <SupplierSelect value={supplier} onChange={setSupplier} />
          </label>

          <label className="input-label">Ordered by
            <input className="input" value={orderedBy} onChange={e=>setOrderedBy(capWords(e.target.value))} placeholder="Name or team" />
          </label>

          <label className="input-label span-all">Notes
            <input className="input" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Optional" />
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">Items</h2>
          <div className="segmented">
            <button className={mode==='single'?'seg active':'seg'} onClick={()=>setMode('single')}>Single</button>
            <button className={mode==='bulk'?'seg active':'seg'} onClick={()=>setMode('bulk')}>Bulk</button>
          </div>
        </div>

        {mode === 'single' && (
          <>
            <div className="line-builder">
              <label className="input-label">Item
                <input
                  list="gfm-order-items"
                  className="input"
                  value={singleItem}
                  onChange={e=>{ setSingleItem(e.target.value); singlePriceTouched.current = false; }}
                  placeholder={loadingInv ? 'Loading inventory' : 'Type or pick an item'}
                  autoFocus
                />
                <datalist id="gfm-order-items">
                  {itemNames.map(n => <option key={n} value={n} />)}
                </datalist>
              </label>

              <label className="input-label">Qty
                <input className="input" type="number" min={1} value={singleQty}
                       onChange={e=>setSingleQty(Math.max(1, Number(e.target.value||1)))} />
              </label>

              <label className="input-label">Unit cost
                <div className="input-wrap"><span className="prefix">£</span>
                  <input className="input" type="number" min="0" step="0.01"
                         value={singleUnitCost}
                         onChange={e=>onSingleUnitCostChange(e.target.value)} />
                </div>
              </label>

              <button className="btn" type="button" disabled={supplierRequired} onClick={submitSingle}>Save order</button>
            </div>
            {packHint(singleMeta, singleQty)}
            {supplierRequired && <div className="line-hint">Choose a supplier to save the order.</div>}
          </>
        )}

        {mode === 'bulk' && (
          <>
            <div className="line-builder">
              <label className="input-label">Item
                <input
                  list="gfm-order-items-bulk"
                  className="input"
                  value={selName}
                  onChange={e=>setSelName(e.target.value)}
                  placeholder={loadingInv ? 'Loading inventory' : 'Type or pick an item'}
                />
                <datalist id="gfm-order-items-bulk">
                  {itemNames.map(n => <option key={n} value={n} />)}
                </datalist>
              </label>

              <label className="input-label">Qty
                <input className="input" type="number" min={1} value={qty} onChange={e=>setQty(Math.max(1, Number(e.target.value||1)))} />
              </label>

              <label className="input-label">Unit cost
                <div className="input-wrap"><span className="prefix">£</span>
                  <input className="input" type="number" min="0" step="0.01"
                         value={unitCost}
                         onChange={e=>setUnitCost(Number(e.target.value||0))} />
                </div>
              </label>

              <button className="btn secondary" type="button" onClick={addLine}>
                <Icon name="plus" size={15} /> Add line
              </button>
            </div>
            {packHint(bulkSelMeta, qty)}
            {bulkSelMeta.unitPrice>0 && Number(unitCost||0)===0 && (
              <div className="line-hint">
                Leave at £0 to use the inventory price (£{bulkSelMeta.unitPrice.toFixed(2)}).
              </div>
            )}

            {!!lines.length && (
              <div className="table-wrap mt-16">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th style={{width:120}}>Qty</th>
                      <th style={{width:170}}>Unit cost</th>
                      <th className="num" style={{width:140}}>Line total</th>
                      <th className="actions" style={{width:110}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(l => {
                      const meta = invIndex.get(l.item.toLowerCase());
                      const packOf = Math.max(1, Number(meta?.packOf || 1));
                      const perEach = packOf>1 && Number(l.unitCost||0)>0
                        ? Number(l.unitCost)/packOf : 0;
                      return (
                        <tr key={l.item}>
                          <td className="cell-strong">
                            {l.item}
                            {packOf>1 && (
                              <span className="badge pack">×{packOf} pack</span>
                            )}
                          </td>
                          <td>
                            <input className="input" type="number" min={1}
                                   value={l.amount}
                                   onChange={e=>updateQty(l.item, e.target.value)}
                                   style={{maxWidth:100}} />
                          </td>
                          <td>
                            <div className="input-wrap" style={{maxWidth:150}}><span className="prefix">£</span>
                              <input className="input" type="number" min="0" step="0.01"
                                     value={l.unitCost}
                                     onChange={e=>updatePrice(l.item, e.target.value)} />
                            </div>
                            {packOf>1 && l.unitCost>0 && (
                              <div className="per-unit">About £{perEach.toFixed(2)} each</div>
                            )}
                          </td>
                          <td className="num">£{(Number(l.amount||0)*Number(l.unitCost||0)).toFixed(2)}</td>
                          <td className="actions">
                            <button className="btn secondary small" onClick={()=>removeLine(l.item)}>Remove</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{textAlign:'right'}}>Total</td>
                      <td className="num">£{totalCost.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {!lines.length && (
              <div className="empty">No items added yet.</div>
            )}

            <div className="form-actions">
              <button className="btn" type="button" disabled={!lines.length || supplierRequired} onClick={submitBulk}>Save order</button>
              <button className="btn secondary" type="button" onClick={()=>nav(-1)}>Cancel</button>
              {supplierRequired && !!lines.length && <span className="hint">Choose a supplier to save the order.</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
